import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { showHubTeamToast } from "../utils/hub_team_toast"

// "Create Report" on the analytics page (TMS-1242). Lives on the page root beside
// share-internally-modal. The report is rendered in the background, so submitting
// posts the request, then polls its status until the file is ready and starts the
// download from this tab; the notification is the fallback if the admin leaves.
//
// The filters are read from the address bar at submit time, not baked into the
// form: the role/date controls live inside a turbo frame that advances the URL,
// while this modal sits outside it and is never re-rendered. `company_id` is the
// staff page's organization (TMS-1244); the org page never has one in its URL.
const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 90000
const FILTER_PARAMS = ["role", "from", "to", "company_id"]

const STATUS_PREPARING = "Preparing your report… This usually takes a few seconds. You can close this window — we'll notify you when it's ready."
const STATUS_FAILED = "We couldn't create your report. Please try again."
const TOAST_DOWNLOADING = "Your report is downloading."
const TOAST_DISMISSED = "We'll notify you when your report is ready."

export default class extends Controller {
  static targets = ["modal", "form", "formats", "format", "status", "submitButton", "submitIcon", "spinner", "submitLabel"]

  connect() {
    this.onShown = () => this.focusFirstFormat()
    this.onHidden = () => this.dismissed()
    if (this.hasModalTarget) {
      this.modalTarget.addEventListener("shown.bs.modal", this.onShown)
      this.modalTarget.addEventListener("hidden.bs.modal", this.onHidden)
    }
  }

  disconnect() {
    this.stopPolling()
    if (this.hasModalTarget) {
      this.modalTarget.removeEventListener("shown.bs.modal", this.onShown)
      this.modalTarget.removeEventListener("hidden.bs.modal", this.onHidden)
    }
  }

  open(event) {
    event.preventDefault()
    if (!this.hasModalTarget) return

    this.reset()
    Modal.getOrCreateInstance(this.modalTarget).show()
  }

  formatChanged() {
    this.submitButtonTarget.disabled = !this.selectedFormat()
  }

  async submit(event) {
    event.preventDefault()
    const format = this.selectedFormat()
    if (!format || this.polling) return

    this.showPreparing()

    let response
    try {
      response = await fetch(this.formTarget.action, {
        method: "POST",
        headers: this.requestHeaders(),
        body: this.requestBody(format)
      })
    } catch {
      this.showFailed()
      return
    }

    if (!response.ok) {
      this.showFailed()
      return
    }

    const report = await response.json()
    this.startPolling(report.status_url, report.download_url)
  }

  // --- states -----------------------------------------------------------------

  reset() {
    this.stopPolling()
    this.formatTargets.forEach((input) => { input.checked = false; input.disabled = false })
    this.submitButtonTarget.disabled = true
    this.setBusy(false)
    this.setStatus(null)
  }

  showPreparing() {
    this.formatTargets.forEach((input) => { input.disabled = true })
    this.submitButtonTarget.disabled = true
    this.setBusy(true)
    this.setStatus(STATUS_PREPARING)
  }

  showFailed() {
    this.stopPolling()
    this.formatTargets.forEach((input) => { input.disabled = false })
    this.submitButtonTarget.disabled = !this.selectedFormat()
    this.setBusy(false)
    this.setStatus(STATUS_FAILED, { error: true })
    showHubTeamToast(STATUS_FAILED, { variant: "error" })
  }

  showReady(downloadUrl) {
    this.stopPolling()
    // A Content-Disposition: attachment response starts the download without
    // leaving the page, so the modal can simply close behind it.
    window.location.assign(downloadUrl)
    showHubTeamToast(TOAST_DOWNLOADING)
    Modal.getOrCreateInstance(this.modalTarget).hide()
  }

  dismissed() {
    const wasWaiting = !!this.polling
    this.reset()
    if (wasWaiting) showHubTeamToast(TOAST_DISMISSED)
  }

  // --- polling ----------------------------------------------------------------

  startPolling(statusUrl, downloadUrl) {
    const startedAt = Date.now()
    const tick = async () => {
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        this.showFailed()
        return
      }

      let status
      try {
        const response = await fetch(statusUrl, { headers: { Accept: "application/json" } })
        if (!response.ok) throw new Error(`status ${response.status}`)
        status = (await response.json()).status
      } catch {
        this.showFailed()
        return
      }

      if (status === "ready") {
        this.showReady(downloadUrl)
      } else if (status === "pending" || status === "generating") {
        this.polling = window.setTimeout(tick, POLL_INTERVAL_MS)
      } else {
        this.showFailed()
      }
    }

    this.polling = window.setTimeout(tick, POLL_INTERVAL_MS)
  }

  stopPolling() {
    if (this.polling) window.clearTimeout(this.polling)
    this.polling = null
  }

  // --- helpers ----------------------------------------------------------------

  selectedFormat() {
    const checked = this.formatTargets.find((input) => input.checked)
    return checked ? checked.value : null
  }

  focusFirstFormat() {
    if (this.formatTargets.length > 0) this.formatTargets[0].focus()
  }

  requestHeaders() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    return {
      Accept: "application/json",
      "Content-Type": "application/x-www-form-urlencoded",
      ...(token ? { "X-CSRF-Token": token } : {})
    }
  }

  requestBody(format) {
    const body = new URLSearchParams({ report_format: format })
    const current = new URL(window.location.href).searchParams
    FILTER_PARAMS.forEach((name) => {
      const value = current.get(name)
      if (value) body.set(name, value)
    })
    return body
  }

  setBusy(busy) {
    this.submitIconTarget.hidden = busy
    this.spinnerTarget.hidden = !busy
    this.submitLabelTarget.textContent = busy ? "Preparing…" : "Download Report"
  }

  setStatus(text, { error = false } = {}) {
    this.statusTarget.hidden = !text
    this.statusTarget.textContent = text || ""
    this.statusTarget.classList.toggle("is-error", !!text && error)
  }
}
